import { motion } from 'framer-motion';
import React, { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import HGeneric from '../helpers/HGeneric';
import { updateStateAttribute } from '../helpers/helper';
import CModal from './CModal';

interface CDropdownPanelProps {
  children?: React.ReactNode;
  onHide: () => void;
  closeOnAnyClick?: boolean;
  openDown: boolean;
  parentRef: RefObject<HTMLDivElement>;
  windowAnchor: 'left' | 'right';
  asModal: boolean;
  /**
   * @deprecated
   */
  usePortal?: boolean;
  style?: Partial<Pick<React.CSSProperties, 'width'> & { left: number; top: number }>;
}

interface State {
  top: number;
  left: number;
  right: number;
  w: number;
  h: number;
  panelW: number;
  panelH: number;
}

export default function CDropdownPanel(props: CDropdownPanelProps) {
  const ref = useRef<HTMLDivElement>(null);
  const lastScrollYRef = useRef<number>(typeof window !== 'undefined' ? window.scrollY : 0);

  const [state, setState] = useState<State>({
    top: 0,
    left: 0,
    right: 0,
    w: 0,
    h: 0,
    panelW: 0,
    panelH: 0
  });

  const classNames = ['absolute bg-white p-2 flex flex-col gap-0 border shadow-lg rounded-lg overflow-y-auto'];

  const computeParentRect = useCallback(() => {
    const r = props.parentRef.current?.getBoundingClientRect();
    if (!r) return;
    updateStateAttribute<State>(
      {
        top: r.top,
        left: r.left,
        right: r.left + r.width,
        w: r.width,
        h: r.height
      },
      setState
    );
  }, [props.parentRef]);

  useEffect(() => {
    if (ref.current) {
      const box = ref.current.getBoundingClientRect();
      updateStateAttribute<State>(
        {
          panelW: box.width,
          panelH: box.height
        },
        setState
      );
    }
    computeParentRect();
  }, [computeParentRect]);

  useEffect(() => {
    const handleGlobalClick = (ev: MouseEvent) => {
      if (!ref.current) return;
      const insidePanel =
        ev.x >= ref.current.getBoundingClientRect().x &&
        ev.x <= ref.current?.getBoundingClientRect().x + ref.current.getBoundingClientRect().width &&
        ev.y >= ref.current.getBoundingClientRect().y &&
        ev.y <= ref.current.getBoundingClientRect().y + ref.current.getBoundingClientRect().height;

      if (!insidePanel) {
        props.onHide();
        return;
      }

      if ((props.closeOnAnyClick ?? false) && insidePanel) {
        props.onHide();
      }
    };

    const onResize = () => {
      props.onHide();
    };

    const onScroll = (ev: Event) => {
      const targetNode = ev.target as Node;
      const insidePanel = !!ref.current?.contains(targetNode);
      if (insidePanel) return;

      const currentY = window.scrollY;

      if (currentY !== lastScrollYRef.current) {
        props.onHide();
        lastScrollYRef.current = currentY;
        return;
      }

      if (props.usePortal) {
        computeParentRect();
      }

      lastScrollYRef.current = currentY;
    };

    const onWheel = (ev: WheelEvent) => {
      const el = ref.current;
      const targetNode = ev.target as Node;
      const insidePanel = !!el?.contains(targetNode);
      if (el && insidePanel) {
        // Prevent wheel scroll from propagating to the page when the panel has no overflow or is at its edge.
        const atTop = el.scrollTop <= 0;
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
        if ((ev.deltaY < 0 && atTop) || (ev.deltaY > 0 && atBottom)) {
          ev.preventDefault();
        }
        return;
      }

      const absY = Math.abs(ev.deltaY);
      const absX = Math.abs(ev.deltaX);

      if (absY > absX && absY > 0) {
        // user tried to move vertically, even if scrollY will snap back
        props.onHide();
      }
    };

    window.addEventListener('click', handleGlobalClick, true);
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      window.removeEventListener('click', handleGlobalClick, true);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('wheel', onWheel);
    };
  }, [props.closeOnAnyClick, props.usePortal, props.parentRef, props.onHide, computeParentRect]);

  useEffect(() => {
    if (props.usePortal) {
      computeParentRect();
    }
  }, [props.usePortal, props.openDown, props.windowAnchor, computeParentRect]);

  const padVal = HGeneric.getRemInPx();

  const topPortal =
    state.top +
    (props.openDown ? state.h + padVal : -state.panelH - padVal) +
    (props.style?.top ?? 0) * (props.openDown ? 1 : -1);

  const leftPortal = state.right - state.panelW;

  const stylePortal = {
    position: 'fixed' as const,
    top: topPortal,
    left: props.style?.left ?? (props.windowAnchor === 'right' ? leftPortal : state.left),
    maxHeight: '35vh',
    width: props.style?.width ?? 'fit-content'
  };

  const parentHeight = props.parentRef.current?.getBoundingClientRect().height ?? 0;

  const styleNonPortal = {
    position: 'absolute' as const,
    bottom: !props.openDown ? parentHeight + 15 : 'auto',
    top: props.openDown ? parentHeight + 15 : 'auto',
    right: 0,
    maxHeight: '35vh',
    width: props.style?.width ?? 'fit-content'
  };

  const style = props.usePortal ? stylePortal : styleNonPortal;

  return (
    <motion.div
      ref={ref}
      style={style}
      className={classNames.join(' ')}
      initial={{ opacity: 0, y: props.openDown ? '-10px' : '10px' }}
      animate={{ opacity: 1, y: '0rem' }}
      exit={{ opacity: 0, y: props.openDown ? '-10px' : '10px' }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      {props.asModal ? (
        <CModal onClose={props.onHide} open={true}>
          {props.children}
        </CModal>
      ) : (
        props.children
      )}
    </motion.div>
  );
}
